// src/data/applicationDesk.js
//
// THE APPLICATION DESK - Part C of data/YCP_PLAN_LOCAL_RECORDS_AND_DESK_2026-09-22.md, as amended by
// RULING 207. Task list section 348.
//
// PURE. It decides what a desk SHOWS and what an officer MAY DO; the callable decides what is written, and
// firestore.rules decides what the database accepts. Three readers, one set of words (directives XI).
//
// THE SHAPE, from the rulings rather than from taste:
//   * ruling 194.3 - the LOCAL Chairperson's desk is an EXCEPTION QUEUE, not a list of applications:
//     "The LC should spend a little time each week reviewing what he needs to".
//   * ruling 207.1 - the GENERAL Chairperson's desk carries EVERY application in his committee.
//   * ruling 207.2 - a question goes to him BEFORE a decision, and no decision may be rendered while one
//     is open.
//   * ruling 207.3 - anything less than approval flags itself to him, automatically.
//   * ruling 193 - the decision stays with the Local Chairperson. Nothing here moves it.
//   * ruling 60.1 / 50(b) - she REFERS; she does not reassign. Reassignment is the GC's act alone.
//   * Record 612 (Bob 10/8) - ruling 291: ONLY the routed Local Chairperson decides; the General Chairperson decides
//     an unrouted application, one whose Local seat was vacant, one the LC referred, and a seniority dispute
//     (applicationApproval.gcDecides). The F19 caps, the action timer, the roster cross-reference, claims held until
//     approval and the 207.3 'Reviewed' act are below.
import { mayDecide, gcDecides } from './applicationApproval';
import { resolveApplicantRouting } from './routing';   // Record 617 (F33): a corrected location re-routes on the same keys

const F = Object.freeze;


// =============================================================================================
// Record 612 (Bob 10/8; F19 agreed, his added concept, rulings 291 and 207.3). The callable (functions\index.js) and
// the desk (src\pages\Applications.js) read everything below - one set of numbers and one set of words (XI).

// ---- (a) CAPS ON THE PUBLIC FORM (F19) -------------------------------------------------------
// The form is App Check-gated, but App Check proves the web app, not the person: a script driving the real page fills
// every desk just the same. So: APPLICATION_CAP_PER_HOUR across the platform, APPLICATION_CAP_PER_LOCAL_DAY for any
// one Local (an unrouted application counts against its committee). submitApplication counts in
// counters/apps_hour_<yyyymmddhh> and counters/apps_<scope>_<yyyymmdd>, in one transaction, refuses the one over the
// cap with CAP_REFUSAL, and tells the administrator the first time a window is hit.
export const APPLICATION_CAP_PER_HOUR = 20;
export const APPLICATION_CAP_PER_LOCAL_DAY = 5;
export const CAP_REFUSAL = 'Too many applications right now. Please try again later.';

// The counter-id segment for a Local. A scopeKey can carry '|' (YM|CSXNL|COLLINWOOD) and firestore.rules splits a
// counter id on '_', so anything but a letter, a digit or '-' becomes '-'. No scopeKey: the committee's unrouted bucket.
export function capScopeKey(scopeKey, generalCommittee) {
  const clean = (v) => String(v == null ? '' : v).trim().replace(/[^A-Za-z0-9-]/g, '-');
  const sk = clean(scopeKey);
  return sk || ('unrouted-' + (clean(generalCommittee) || 'none'));
}

// The counts are those BEFORE this application. '' = under both caps; 'hour' or 'local' = the cap it would break.
export function applicationCapHit(hourCount, localCount) {
  if (Number(hourCount || 0) >= APPLICATION_CAP_PER_HOUR) return 'hour';
  if (Number(localCount || 0) >= APPLICATION_CAP_PER_LOCAL_DAY) return 'local';
  return '';
}

// ---- (b) ABEYANCE WITH A TIMER (Bob's added concept) -------------------------------------------
// Every application is held with the routed Local Chairperson with an action date APPLICATION_ACTION_DAYS calendar
// days after it was submitted. submitApplication stamps actionDueAt from the server clock; one written before that
// field existed is read from its submittedAt. The LC desk shows the days left. Past the date it is flagged to the
// General Chairperson through ruling 207.3's mechanism (gcFlag, gcFlagReason OVERDUE_REASON) - by the
// flagOverdueApplications sweep, and on his desk at once through flagStanding(), so a sweep that has not run yet
// never hides an overdue application. An application the GC decides (gcDecides, ruling 291) or a claimed office is
// not on the LC's timer. PURE: no clock is read here - the caller passes nowMs, and without it nothing is overdue.
export const APPLICATION_ACTION_DAYS = 7;
export const OVERDUE_REASON = 'LC action overdue';
const DAY_MS = 86400000;
function msOf(t) {
  if (t === null || t === undefined || t === '') return 0;
  if (typeof t === 'number') return Number.isFinite(t) ? t : 0;
  if (typeof t.toMillis === 'function') return t.toMillis();
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  if (typeof t._seconds === 'number') return t._seconds * 1000;
  if (typeof t === 'string') { const p = Date.parse(t); return Number.isFinite(p) ? p : 0; }
  return 0;
}

export function actionDueMs(submittedMs) {
  return msOf(submittedMs) + APPLICATION_ACTION_DAYS * DAY_MS;
}

// { dueMs, dueDate, daysLeft, overdue, label }, or null where the application carries no date to count from.
export function actionClock(application, nowMs) {
  const a = application || {};
  const due = msOf(a.actionDueAt) || (msOf(a.submittedAt) ? actionDueMs(msOf(a.submittedAt)) : 0);
  if (!due) return null;
  const dueDate = new Date(due).toISOString().slice(0, 10);
  if (typeof nowMs !== 'number' || !Number.isFinite(nowMs)) {
    return { dueMs: due, dueDate, daysLeft: null, overdue: false, label: 'Action due by ' + dueDate };
  }
  const overdue = nowMs > due;
  const daysLeft = Math.ceil((due - nowMs) / DAY_MS);
  const late = Math.max(1, Math.ceil((nowMs - due) / DAY_MS));
  const label = overdue
    ? 'Action overdue by ' + late + (late === 1 ? ' day' : ' days') + ' (due ' + dueDate + ') - flagged to the General Chairperson'
    : (daysLeft <= 1 ? 'Action due within a day (by ' + dueDate + ')' : daysLeft + ' days left to act (due ' + dueDate + ')');
  return { dueMs: due, dueDate, daysLeft, overdue, label };
}

// Is this application on the routed Local Chairperson's timer at all?
export function lcHolds(application) {
  const a = application || {};
  if (a.status !== 'pending') return false;
  const office = String(a.claimedOffice || 'none');
  if (office === 'local_chair' || office === 'general_chair') return false;
  return !gcDecides(a);
}

export function lcOverdue(application, nowMs) {
  if (typeof nowMs !== 'number' || !lcHolds(application)) return false;
  const c = actionClock(application, nowMs);
  return !!(c && c.overdue);
}

// The 207.3 flag as the GC's desk must show it: a written flag not yet reviewed, or an overdue LC timer the sweep has
// not yet written (overdueFlaggedAt marks one it has - so a flag he then reviews is not raised again).
export function flagStanding(application, nowMs) {
  const a = application || {};
  if (a.gcFlag && !a.gcFlagCleared) return true;
  return lcOverdue(a, nowMs) && !a.overdueFlaggedAt;
}

export function flagReasonOf(application, nowMs) {
  const a = application || {};
  if (a.gcFlag && !a.gcFlagCleared) return String(a.gcFlagReason || 'less than approval');
  return (lcOverdue(a, nowMs) && !a.overdueFlaggedAt) ? OVERDUE_REASON : '';
}

// ---- (c) THE SENIORITY ROSTER CROSS-REFERENCE (ruling 194) - NEVER A BLOCK ---------------------
// `roster` is a seniority roster the platform holds: { file, date, rows: [{ rosterNumber, seniorityDate, name }] },
// or null. On 2026-10-08 the platform holds NONE: src\data\seniorityRosters.js is the work locations a roster spans,
// data\YCP_LC_Roster_GO342.csv is the Local Chairpersons' roster, and no file in data\ lists members by roster number
// and seniority date. So today the answer is NOT PERFORMED, said in words, and isException() keeps reading that as
// "not a pass". The result feeds indicatorRows() and the exception queue and nothing else: no act is refused on it.
export function rosterCrossReference(application, roster) {
  const a = application || {};
  const r = (roster && Array.isArray(roster.rows) && roster.rows.length) ? roster : null;
  if (!r) {
    return { performed: false, file: '', date: '', fields: {}, match: null,
      summary: 'Not checked - no seniority roster for this craft is held on the platform.' };
  }
  const file = String(r.file || '');
  const date = String(r.date || '');
  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const num = norm(a.rosterNumber);
  const row = num ? (r.rows.find((x) => x && norm(x.rosterNumber) === num) || null) : null;
  if (!row) {
    return { performed: true, file, date, match: null,
      fields: { rosterNumber: { matched: false, rosterValue: '' } },
      summary: 'Roster number ' + (String(a.rosterNumber || '').trim() || '(none given)') + ' is not on '
        + (file || 'the roster') + (date ? ' dated ' + date : '') + '.' };
  }
  const sdOk = norm(row.seniorityDate) === norm(a.seniorityDate);
  return { performed: true, file, date,
    match: { rosterNumber: String(row.rosterNumber), seniorityDate: String(row.seniorityDate || ''), name: String(row.name || '') },
    fields: { rosterNumber: { matched: true, rosterValue: String(row.rosterNumber) },
              seniorityDate: { matched: sdOk, rosterValue: String(row.seniorityDate || '') } },
    summary: sdOk
      ? 'Matches ' + (file || 'the roster') + (date ? ' dated ' + date : '') + ': number ' + row.rosterNumber
        + ', seniority date ' + (row.seniorityDate || '') + '.'
      : 'Roster number ' + row.rosterNumber + ' is on ' + (file || 'the roster') + ', but its seniority date there is '
        + (row.seniorityDate || '(blank)') + '.' };
}

// ---- (d) CLAIMS HELD UNTIL APPROVAL ------------------------------------------------------------
// An applicant has NO ACCOUNT (ruling 193.6: applying is not joining; firestore.rules: "An applicant has no account
// yet and therefore reads nothing here"), so he cannot file or hold a draft. He may record the FACTS of a claim on his
// application - date, place, what happened, and the kind - up to MAX_PENDING_CLAIMS. They wait on the application, the
// Local Chairperson sees them, and on approval decideApplication turns each into a DRAFT claim owned by the new member
// (pendingClaimDrafts). Holding them here does not stop any time limit; the form says so.
export const MAX_PENDING_CLAIMS = 5;
export const PENDING_CLAIM_LIMITS = F({ location: 120, whatHappened: 4000, kind: 40 });

export function validatePendingClaims(list) {
  const no = (why) => ({ ok: false, why, clean: [] });
  if (list === undefined || list === null) return { ok: true, why: '', clean: [] };
  if (!Array.isArray(list)) return no('The claims recorded with an application must be a list.');
  if (list.length > MAX_PENDING_CLAIMS) {
    return no('Up to ' + MAX_PENDING_CLAIMS + ' claims can be recorded with an application. Your Local Chairperson can take the rest.');
  }
  const clean = [];
  for (let i = 0; i < list.length; i++) {
    const c = list[i];
    const n = 'Claim ' + (i + 1) + ': ';
    if (!c || typeof c !== 'object' || Array.isArray(c)) return no(n + 'it is not a claim.');
    const date = String(c.date == null ? '' : c.date).trim();
    const location = String(c.location == null ? '' : c.location).trim();
    const whatHappened = String(c.whatHappened == null ? '' : c.whatHappened).trim();
    const kind = String(c.kind == null ? '' : c.kind).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || Number.isNaN(Date.parse(date + 'T00:00:00Z'))) return no(n + 'give the date it happened.');
    if (!location) return no(n + 'say where it happened.');
    if (location.length > PENDING_CLAIM_LIMITS.location) return no(n + 'the place is longer than ' + PENDING_CLAIM_LIMITS.location + ' characters.');
    if (!whatHappened) return no(n + 'say what happened.');
    if (whatHappened.length > PENDING_CLAIM_LIMITS.whatHappened) return no(n + 'what happened is longer than ' + PENDING_CLAIM_LIMITS.whatHappened + ' characters.');
    if (kind && (kind.length > PENDING_CLAIM_LIMITS.kind || !/^[a-z_]+$/.test(kind))) return no(n + 'that is not a kind of claim this form offers.');
    clean.push({ date, location, whatHappened, kind });
  }
  return { ok: true, why: '', clean };
}

// Bob 2026-10-12: "He fills out his application and is approved to use the platform. What did you do with his application
// information? why isnt that automatically transferred to his 1st claim (and every subsequent claim if he doesnt change
// it)". The approval wrote nothing to his profile, and every claim form pre-fills from the profile (NewClaim.js, QuickFile.js,
// ClaimFormActions.js). These two put the application into the profile at approval.
//
// The application asks for the mailing address as one line; the profile and the claim keep street / city / state / ZIP.
// Split only when the line reads plainly as "street, city, ST 12345" (a comma before the city); otherwise the whole line
// goes into street and the rest stays blank - the claim form then asks him for what is missing, once, and saves it to his
// profile. Nothing is guessed.
export function splitMailingAddress(line) {
  const t = String(line == null ? '' : line).replace(/\s+/g, ' ').trim();
  if (!t) return { street: '', city: '', state: '', zip: '' };
  const m = t.match(/^(.+),\s*([^,]+?),?\s+([A-Za-z]{2})\.?,?\s+(\d{5}(?:-\d{4})?)$/);
  if (m) return { street: m[1].trim(), city: m[2].trim(), state: m[3].toUpperCase(), zip: m[4] };
  return { street: t, city: '', state: '', zip: '' };
}

// The profile fields an approved application fills (users/{uid}; the keys firestore.rules memberWritableOnly() already
// lets him change himself). A field his profile already holds is left alone - an adopted account keeps what he wrote.
export function profileFromApplication(application, existingProfile) {
  const a = application || {}, p = existingProfile || {};
  const s = (x) => String(x == null ? '' : x).trim();
  const addr = splitMailingAddress(a.mailingAddress);
  const want = {
    name: s(a.fullLegalName), email: s(a.email).toLowerCase(), phone: s(a.mobile),
    street: addr.street, city: addr.city, state: addr.state, zip: addr.zip,
    rosterNumber: s(a.rosterNumber), seniorityDate: s(a.seniorityDate), employeeNumber: s(a.employeeNumber),
    claimantJobSymbol: s(a.jobSymbol), craft: s(a.craftAndClass), railroad: s(a.railroad), workLocation: s(a.workLocation),
  };
  const patch = {};
  Object.keys(want).forEach((k) => { if (want[k] && !s(p[k])) patch[k] = want[k]; });
  return patch;
}

// The draft claims an approval creates, one per recorded claim, in the draft shape claimPayload.buildClaimPayload
// writes (the contact block always present, routing as the application was routed), owned by the NEW member and
// marked where it came from. `member` is { uid, email } of the account the approval opened or adopted; no uid, no
// drafts. The caller adds createdAt / updatedAt from the server clock.
export function pendingClaimDrafts(application, member) {
  const a = application || {};
  const m = member || {};
  const uid = String(m.uid || '');
  if (!uid) return [];
  const v = validatePendingClaims(a.pendingClaims);
  if (!v.ok) return [];
  const s = (x) => String(x == null ? '' : x).trim();
  const committee = s(a.generalCommittee);
  const scopeKey = s(a.scopeKey);
  const email = s(m.email || a.email);
  const addr = splitMailingAddress(a.mailingAddress);   // Bob 2026-10-12: his application's address goes on his first claims
  return v.clean.map((c) => {
    const d = {
      claimantName: s(a.fullLegalName), claimantEmail: email, claimantPhone: s(a.mobile),
      claimantStreet: addr.street, claimantCity: addr.city, claimantState: addr.state, claimantZip: addr.zip,
      claimantJobSymbol: s(a.jobSymbol),
      rosterNumber: s(a.rosterNumber), seniorityDate: s(a.seniorityDate), employeeNumber: s(a.employeeNumber),
      craft: s(a.craftAndClass), railroad: s(a.railroad), workLocation: s(a.workLocation),
      eventLocation: c.location, incidentDate: c.date, description: c.whatHappened,
      matchedRules: [], violationTypes: [], approvedNarrative: '', applicableAgreement: '',
      scopeKey: scopeKey || ((committee || 'UNKNOWN') + '|UNROUTED'),
      routingStatus: scopeKey ? 'routed' : 'unrouted',
      committee, localCommittee: s(a.localCommittee),
      createdBy: uid, claimantUid: uid, filedBy: email, profileConfirmed: false,
      amountClaimed: 0, priority: 'standard', notes: [], documents: [], boardAwards: [], exhibits: [],
      isTestData: false, status: 'draft', title: c.whatHappened.slice(0, 60) || 'Untitled draft',
      enteredBeforeApproval: true, fromApplicationId: s(a.id),
    };
    if (c.kind) d.claimKind = c.kind;
    return d;
  });
}

// ---------------------------------------------------------------------------------------------
// THE ROW. The indicators Bob named on 2026-09-22: what proves "the applicant is in the right area -
// properly and accurately matched to the same gca". Matched reads black; unmatched reads bold red
// (ruling 194.2), which is why every row carries `matched` rather than a colour.
export const INDICATORS = F(['posting', 'local', 'committee', 'rosterNumber', 'seniorityDate', 'routing', 'obligation']);

// `xref` is the roster cross-reference result, or null where none was performed. NOT PERFORMED is never
// reported as a pass (ruling 194: "It never reports a pass it did not make").
export function indicatorRows(application, xref, obligation) {
  const a = application || {};
  const x = xref || null;
  const field = (k) => (x && x.fields && x.fields[k]) || null;
  const rosterRow = (k, label, value) => {
    const f = field(k);
    return {
      key: k, label, value: value || '',
      matched: f ? f.matched === true : null,          // null = not checked, and it says so
      note: f ? (f.matched ? 'matches the roster' : 'does not match the roster: ' + (f.rosterValue || 'no value on the roster'))
              : 'not checked - no seniority roster is on file for this craft',
    };
  };
  return [
    { key: 'posting', label: 'Posting', value: [a.craftAndClass, a.railroad, a.workLocation].filter(Boolean).join(' / '), matched: null, note: 'as he entered it' },
    { key: 'local', label: 'Local', value: a.localCommittee ? 'Local ' + a.localCommittee : '', matched: a.routingStatus === 'routed' ? true : null,
      note: a.routingStatus === 'routed' ? 'resolved from the posting' : 'no Local resolved - assigned by the General Chairperson (doctrine 9)' },
    { key: 'committee', label: 'General Committee', value: a.generalCommittee || '', matched: a.generalCommittee ? true : false,
      note: a.generalCommittee ? 'resolved from craft and railroad' : 'no committee resolved' },
    rosterRow('rosterNumber', 'Roster number', a.rosterNumber),
    rosterRow('seniorityDate', 'Seniority date', a.seniorityDate),
    { key: 'routing', label: 'Routing', value: a.routingStatus || '', matched: a.routingStatus === 'routed',
      note: a.routingStatus === 'routed' ? 'on the routed Local Chairperson’s desk' : 'with the General Chairperson, unrouted' },
    { key: 'obligation', label: 'Obligation', value: obligation && obligation.obligationDate ? obligation.obligationDate : 'outstanding',
      matched: obligation ? true : null, note: obligation ? 'taken and on record' : 'not yet taken - the local convenes it [R-208.3]' },
  ];
}

// ---------------------------------------------------------------------------------------------
// THE EXCEPTION QUEUE (ruling 194.3). An application is an exception when something needs a person:
// a value that did not match, a check that could not run, no Local resolved, a claimed office, or an open
// question. Everything else carries itself, and does not take his week.
export function isException(application, xref) {
  const a = application || {};
  if (a.status !== 'pending') return false;
  if (a.openQuestionId) return true;
  if (a.routingStatus !== 'routed') return true;
  if (String(a.claimedOffice || 'none') !== 'none') return true;
  if (!xref || xref.performed !== true) return true;                 // not performed is not a pass
  return Object.keys(xref.fields || {}).some((k) => xref.fields[k] && xref.fields[k].matched === false);
}

export function exceptionReasons(application, xref) {
  const a = application || {};
  const out = [];
  if (a.openQuestionId) out.push('a question is with the General Chairperson');
  if (a.routingStatus !== 'routed') out.push('no Local resolved from the posting');
  if (String(a.claimedOffice || 'none') !== 'none') out.push('he names an office: ' + a.claimedOffice);
  if (!xref || xref.performed !== true) out.push('the roster cross-reference has not been performed');
  else Object.keys(xref.fields || {}).forEach((k) => { if (xref.fields[k] && xref.fields[k].matched === false) out.push(k + ' does not match the roster'); });
  return out;
}

// ---------------------------------------------------------------------------------------------
// THE ACTS. `flagsGc` is ruling 207.3 - anything less than approval flags itself. `blockedByQuestion` is
// 207.2 - a decision cannot be rendered while a question is open; asking and answering never are.
export const ACTS = F({
  approve:  F({ label: 'Approve',                    flagsGc: false, blockedByQuestion: true,  letter: 'approval' }),
  refuse:   F({ label: 'Refuse, with the reason',    flagsGc: true,  blockedByQuestion: true,  letter: 'refusal' }),
  refer:    F({ label: 'Refer to the General Chairperson', flagsGc: true, blockedByQuestion: false, letter: 'referral' }),
  correct:  F({ label: 'Correct a value from the roster',  flagsGc: true, blockedByQuestion: false, letter: 'correction' }),
  ask_gc:   F({ label: 'Ask the General Chairperson', flagsGc: true,  blockedByQuestion: false, letter: null }),
  answer:   F({ label: 'Answer the question',         flagsGc: false, blockedByQuestion: false, letter: null }),
  // Record 612: Bob's item 10 - the Local Chairperson marks a seniority dispute, which refers it to the GC (gcDecides).
  seniority_dispute: F({ label: 'Refer as a seniority dispute', flagsGc: true, blockedByQuestion: false, letter: 'referral' }),
  // Record 612: ruling 207.3 'Reviewed' (Bob: okay) - the committee GC clears a standing flag, with a short note.
  review:   F({ label: 'Reviewed - clear the flag',  flagsGc: false, blockedByQuestion: false, letter: null }),
});

export const ACT_KINDS = F(Object.keys(ACTS));

// RULING 207.2, and the single place that states it: while a question is open, no decision is rendered.
export function decisionBlocked(application) {
  const a = application || {};
  if (!a.openQuestionId) return { blocked: false, why: '' };
  return { blocked: true, why: 'A question is with the General Chairperson. Ruling 207: it is discussed with him before any decision is rendered.' };
}

// Who may do what. The decision acts run through mayDecide() - the office spine ONB-4 already uses, so a
// desk cannot widen it. Asking is the Local Chairperson's; answering is the General Chairperson's.
// Record 612 (ruling 291, Bob item 10, ruling 207.3): refer and seniority_dispute are the routed Local Chairperson's -
// each puts the decision with his General Chairperson (gcDecides); correct is any officer the application is in front
// of; neither is a decision, so ruling 291 does not narrow them. 'review' (Reviewed) is the committee General
// Chairperson's or the administrator's, on a standing written flag, WHATEVER the status - a refused application is
// exactly what it clears.
export function mayAct(actor, application, kind) {
  const a = actor || {};
  const spec = ACTS[kind];
  if (!spec) return { ok: false, reason: '"' + kind + '" is not an act on the application path.' };
  const app = application || {};
  const scopes = Array.isArray(a.scopes) ? a.scopes : [];
  const lcOfIt = a.role === 'local_chair' && !!app.scopeKey && scopes.indexOf(app.scopeKey) !== -1;
  const gcOfIt = a.role === 'general_chair' && !!a.committee && a.committee === app.generalCommittee;

  if (kind === 'review') {
    if (!(a.role === 'admin' || gcOfIt)) return { ok: false, reason: 'Only the General Chairperson of the committee, or the administrator, marks a flag reviewed.' };
    if (!app.gcFlag || app.gcFlagCleared) return { ok: false, reason: 'There is no standing flag on that application to review.' };
    return { ok: true };
  }
  if (app.status && app.status !== 'pending') return { ok: false, reason: 'That application was already ' + app.status + '.' };

  if (kind === 'answer') {
    const ok = a.role === 'admin' || (a.role === 'general_chair' && a.committee && a.committee === app.generalCommittee);
    return ok ? { ok: true } : { ok: false, reason: 'Only the General Chairperson of the committee answers the question put to him.' };
  }
  if (kind === 'ask_gc') {
    const ok = a.role === 'admin'
      || (a.role === 'local_chair' && !!app.scopeKey && scopes.indexOf(app.scopeKey) !== -1)
      || (a.role === 'general_chair' && a.committee === app.generalCommittee);
    return ok ? { ok: true } : { ok: false, reason: 'Only an officer this application is in front of may put a question about it.' };
  }
  if (kind === 'refer' || kind === 'seniority_dispute') {
    if (!(a.role === 'admin' || lcOfIt)) return { ok: false, reason: 'Only the Local Chairperson the application is routed to refers it to his General Chairperson.' };
    if (kind === 'refer' && app.referredAt) return { ok: false, reason: 'It was already referred to the General Chairperson.' };
    if (kind === 'seniority_dispute' && app.seniorityDispute === true) return { ok: false, reason: 'It is already marked as a seniority dispute.' };
    return { ok: true };
  }
  if (kind === 'correct') {
    const ok = a.role === 'admin' || lcOfIt || gcOfIt;
    return ok ? { ok: true } : { ok: false, reason: 'Only an officer this application is in front of corrects it.' };
  }
  const may = mayDecide(a, app);
  if (!may.ok) return { ok: false, reason: may.reason };
  if (spec.blockedByQuestion) {
    const b = decisionBlocked(app);
    if (b.blocked) return { ok: false, reason: b.why };
  }
  return { ok: true, rung: may.rung };
}

// What the desk offers on a row: every act, each with whether it is available and why not.
export function actsFor(actor, application) {
  return ACT_KINDS.map((kind) => {
    const m = mayAct(actor, application, kind);
    return { kind, label: ACTS[kind].label, available: m.ok, why: m.ok ? '' : m.reason, flagsGc: ACTS[kind].flagsGc, letter: ACTS[kind].letter };
  });
}

// ---------------------------------------------------------------------------------------------
// THE TWO DESKS. The Local Chairperson sees exceptions (194.3); the General Chairperson sees EVERY
// application in his committee (207.1), with the flagged ones first (207.3).
// Record 612 (Bob 10/8): every row carries its action clock (abeyance with a timer). An application past its action
// date comes onto the Local Chairperson's queue even when it is otherwise clean - it is waiting on him - and is flagged
// on the General Chairperson's desk (flagStanding) with OVERDUE_REASON. nowMs is the caller's clock; without it no
// clock is computed and nothing is overdue (this module reads no clock).
export function localDesk(applications, xrefs, nowMs) {
  const x = xrefs || {};
  return (Array.isArray(applications) ? applications : [])
    // Bob 10/8 (F19): "all applications are held in abeyance with the LC with a timer" - EVERY pending application is on
    // his queue with its clock; a clean one says so instead of being hidden (supersedes 194.3's exceptions-only queue).
    .filter((a) => a && a.status === 'pending')
    .map((a) => ({ application: a,
      reasons: (isException(a, x[a.id]) ? exceptionReasons(a, x[a.id]) : ['every value matches - waiting on your decision'])
        .concat(lcOverdue(a, nowMs) ? ['past its action date - flagged to the General Chairperson'] : []),
      clock: actionClock(a, nowMs) }));
}

export function generalDesk(applications, xrefs, nowMs) {
  const x = xrefs || {};
  const rows = (Array.isArray(applications) ? applications : [])
    // RECORD 588 (ruling 207.3 audit): a refused application is decided, not pending - and it is exactly what 207.3
    // flags. It stays on his desk while its flag stands: pending, OR gcFlag && !gcFlagCleared.
    // Record 612: "its flag stands" is flagStanding() - the written flag, or an overdue LC timer not yet written.
    .filter((a) => a && (a.status === 'pending' || flagStanding(a, nowMs)))
    .map((a) => ({ application: a, flagged: flagStanding(a, nowMs), flagReason: flagReasonOf(a, nowMs),
      clock: a.status === 'pending' ? actionClock(a, nowMs) : null,
      reasons: a.status === 'pending' ? exceptionReasons(a, x[a.id]) : ['decided: ' + a.status] }));
  // Flagged first; within each group, oldest first - a man waiting longest is seen first.
  const at = (r) => (r.application.submittedAt && r.application.submittedAt.seconds) || 0;
  return rows.sort((p, q) => (p.flagged === q.flagged ? at(p) - at(q) : (p.flagged ? -1 : 1)));
}

// ---------------------------------------------------------------------------------------------
// Record 617 (F33, 10/7 audit): A CORRECTED ROUTING INPUT RE-ROUTES. The application was routed at submission on
// craft x railroad x work location (resolveApplicantRouting, section 341), and workLocation is correctable from the
// roster (applicationAct CORRECTABLE) - but a corrected location left the scopeKey where the wrong one had put it.
// correctionRoute() recomputes the route with the SAME function the submission used. Within the committee it returns
// the new Local for the callable to write (routingStatus then comes from applicationRecord.routeApplication, which
// needs the seat holder - a Firestore read - so it is the callable's). A location belonging to ANOTHER committee is
// refused: the applicant re-applies there; an application is never moved across committees by a correction.
// PURE. `changed` says whether the scopeKey moves; nothing is re-routed when it does not.
export const ROUTING_INPUTS = F(['craftAndClass', 'railroad', 'workLocation']);
export const REROUTE_REFUSAL = 'This location belongs to another committee - the applicant must re-apply there';
export function correctionRoute(application, field, value) {
  const a = application || {};
  if (ROUTING_INPUTS.indexOf(field) === -1) return { ok: true, reroute: false, changed: false };
  const n = Object.assign({}, a, { [field]: value });
  const d = resolveApplicantRouting(n.craftAndClass, n.railroad, n.workLocation);
  if (String(d.generalCommittee || '') !== String(a.generalCommittee || '')) {
    return { ok: false, reroute: true, changed: false, why: REROUTE_REFUSAL, committee: String(d.generalCommittee || '') };
  }
  const scopeKey = String(d.scopeKey || '');
  return { ok: true, reroute: true, changed: scopeKey !== String(a.scopeKey || ''),
    scopeKey, localCommittee: String(d.localCommittee || ''), generalCommittee: String(d.generalCommittee || '') };
}
